import { FulfillmentWindow, RequiresActivationConstraintWithLimits } from '@occc/feat-constraints';
import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';

import { LoadingOverlay } from '@occc-app/design-system';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import { OfferFilter, Offer_Engagement_Type } from '@occc/graphql';
import { AwardType, Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { EarningMechanismAwardSelector } from '@occc-app/offerHelpers/components/earning-mechanism-award-selector';
import { EitherActivationOrAwardCount } from '@occc-app/routes/Promotion/components/PromotionConfigureForm/EitherActivationOrAwardCountProps';
import { VoucherExpirationDaysRedemption } from '@occc-app/routes/CampaignComponents/VoucherExpirationDaysRedemption';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { VisibleForCommandCenterFulfillment } from '@occc-app/routes/CampaignComponents/components/VisibleForCommandCenterFulfillment';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';

import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { LoyaltyEventConfiguration } from './LoyaltyEventConfiguration';

import { validateConfiguration } from '../../validation';
import {
  LoyaltyEventConfigureFormInitialValues,
  LoyaltyEventConfigureFormValues,
} from '../../types';

type LoyaltyEventConfigureFormProps = {
  onSubmit: (
    values: LoyaltyEventConfigureFormValues,
    form: FormApi<LoyaltyEventConfigureFormValues, LoyaltyEventConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: LoyaltyEventConfigureFormInitialValues;
  makeOffersFilter: (search: string) => OfferFilter;
  loading: boolean;
  currentFlowType: Flow_Types;
  onNextStepButtonClick?: () => void;
  isFormEditable: boolean;
};

export const LoyaltyEventConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  makeOffersFilter,
  loading,
  currentFlowType,
  onNextStepButtonClick,
  isFormEditable,
}: LoyaltyEventConfigureFormProps) => {
  const renderForm = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      form,
      dirty,
    }: FormRenderProps<
      LoyaltyEventConfigureFormValues,
      LoyaltyEventConfigureFormInitialValues
    >) => {
      const isVoucherRedemptionShown = values.awardType === AwardType.AwardVouchers;
      const isVoucherRedemptionApplicable =
        values.awardVouchers?.voucheredOffer?.engagementType === Offer_Engagement_Type.Vouchered;
      const isVoucherRedemptionEnabled = values.awardVouchers?.isEnabled ?? false;

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          onCancel={() => onCancelClick(dirty)}
          currentFlowType={currentFlowType}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={onCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      return (
        <OfferForm dirty={dirty} handleSubmit={handleSubmit}>
          <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
            <OfferEarningMechanismConfigurationSection>
              <Box mb={2}>
                <OfferNameAndDescriptionConfiguration
                  entityName='Loyalty Event Offer'
                  title='Start by setting a name and description for your loyalty event offer'
                />
              </Box>
              <Grid container spacing={3}>
                <Grid xs={12}>
                  <EarningMechanismAwardSelector
                    buttonTypes={[
                      AwardType.AwardNullable,
                      AwardType.AwardPoints,
                      AwardType.AwardVouchers,
                    ]}
                    makeOffersFilter={makeOffersFilter}
                    values={values}
                  />
                </Grid>
                <Grid xs={12}>
                  <LoyaltyEventConfiguration />
                </Grid>
              </Grid>
            </OfferEarningMechanismConfigurationSection>

            <OfferConfigurationSection title='Limits'>
              <AwardCount values={values} />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Requirements'>
              {isVoucherRedemptionShown && (
                <VoucherExpirationDaysRedemption
                  isVoucherRedemptionEnabled={isVoucherRedemptionEnabled}
                  isVoucherRedemptionApplicable={isVoucherRedemptionApplicable}
                  showAvatar
                />
              )}

              <VisibleForCommandCenterFulfillment />

              <FulfillmentWindow values={values} />

              <RequiresActivationConstraintWithLimits values={values.activation} />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
              <CustomData values={values.customData} />
            </OfferConfigurationSection>
          </OfferConfigurationWizardFormLayout>
          <EitherActivationOrAwardCount form={form} />
        </OfferForm>
      );
    },
    [isFormEditable, currentFlowType, onCancelClick, onNextStepButtonClick, makeOffersFilter],
  );
  return (
    <Box position='relative'>
      <Form<LoyaltyEventConfigureFormValues, LoyaltyEventConfigureFormInitialValues>
        onSubmit={onSubmit}
        render={renderForm}
        mutators={{
          ...arrayMutators,
          setFieldValue: (args, state, utils) => {
            // name, value
            utils.changeValue(state, args[0], () => args[1]);
          },
        }}
        initialValues={initialValues}
        validate={validateConfiguration}
      />
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
